/**
 * Phase 31C: SVG factory floor renderer.
 *
 * - Machine nodes with state-colour fills and smooth CSS transitions
 * - Queue bubbles with pop-in animation
 * - Conveyor edges with smart bezier routing (horizontal if possible, arced if back-flow)
 * - Off-shift machines visually distinct (dim + diagonal stripe + badge)
 * - Machine click opens InspectorPanel
 * - Order particles: circles fly from source → machines along conveyor beziers,
 *   hold as static dots while in-service, and pulse at the next machine while queued.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSimStore } from '../store/simStore'
import { familyColour } from '../lib/colours'
import type { FactorySnapshot, MachineSnapshot } from '../types'

// ── Layout constants ──────────────────────────────────────────────────────────
const MACHINE_W   = 136
const MACHINE_H   = 68
const PILL_H      = 16
const PILL_W      = 80
const INFO_ROW_Y  = MACHINE_H + 8
const BADGE_ROW_Y = MACHINE_H + 29
const SUB_ROWS_H  = 50
const PAD         = 60    // top / bottom viewBox padding
const PAD_LEFT    = 120   // left padding — extra room for source node + particles
const PAD_RIGHT   = 120   // right padding — extra room for sink node + particles

// ── Particle constants ────────────────────────────────────────────────────────
const DIAMOND_R  = 11    // source diamond half-size (px)
const TRANSIT_MS = 500   // particle animation duration ≈ poll interval (ms)

// ── State colour palette ──────────────────────────────────────────────────────
const FILL: Record<string, string> = {
  idle:      '#1e293b',
  busy:      '#0c4a3a',
  setup:     '#431407',
  failed:    '#3b0a1a',
  off_shift: '#111827',
}
const STROKE: Record<string, string> = {
  idle:      '#334155',
  busy:      '#2dd4bf',
  setup:     '#f59e0b',
  failed:    '#f43f5e',
  off_shift: '#1f2937',
}
const LABEL_COLOR: Record<string, string> = {
  idle:      '#94a3b8',
  busy:      '#ccfbf1',
  setup:     '#fef3c7',
  failed:    '#fecdd3',
  off_shift: '#374151',
}
const STATE_BADGE: Record<string, string> = {
  idle:      'IDLE',
  busy:      'BUSY',
  setup:     'SETUP',
  failed:    '⚠ FAILED',
  off_shift: '◌ OFF',
}

// ── Geometry types ────────────────────────────────────────────────────────────
interface Pt { x: number; y: number }
interface BezierSeg { p0: Pt; p1: Pt; p2: Pt; p3: Pt }

interface Transit {
  key:       string
  orderId:   string
  label:     string
  colour:    string
  seg:       BezierSeg
  startedAt: number   // performance.now()
  duration:  number   // ms
}

// ── Bezier helpers ────────────────────────────────────────────────────────────

function evalBezier(t: number, { p0, p1, p2, p3 }: BezierSeg): Pt {
  const u = 1 - t
  return {
    x: u**3*p0.x + 3*u**2*t*p1.x + 3*u*t**2*p2.x + t**3*p3.x,
    y: u**3*p0.y + 3*u**2*t*p1.y + 3*u*t**2*p2.y + t**3*p3.y,
  }
}

/** Smoothstep easing: 0 → 1 with zero derivative at both ends. */
const smoothstep = (t: number) => t * t * (3 - 2 * t)

/**
 * Compute bezier control points for a machine-to-machine conveyor edge.
 * Mirrors the routing logic in ConveyorLine exactly so particles follow the same path.
 */
function machineToMachineBezier(src: MachineSnapshot, dst: MachineSnapshot): BezierSeg {
  const srcCX = src.x + MACHINE_W / 2
  const dstCX = dst.x + MACHINE_W / 2
  const dx    = dst.x - (src.x + MACHINE_W)

  if (dx >= -20) {
    // Forward / parallel: right edge → left edge
    const sx = src.x + MACHINE_W,  sy = src.y + MACHINE_H / 2
    const ex = dst.x,              ey = dst.y + MACHINE_H / 2
    const s  = Math.max(60, Math.abs(ex - sx) * 0.45)
    return { p0:{x:sx,y:sy}, p1:{x:sx+s,y:sy}, p2:{x:ex-s,y:ey}, p3:{x:ex,y:ey} }
  }
  if (Math.abs(srcCX - dstCX) < 30) {
    // Same column: bottom edge → top edge
    const sx = src.x + MACHINE_W / 2, sy = src.y + MACHINE_H
    const ex = dst.x + MACHINE_W / 2, ey = dst.y
    const s  = Math.max(40, Math.abs(ey - sy) * 0.35)
    return { p0:{x:sx,y:sy}, p1:{x:sx,y:sy+s}, p2:{x:ex,y:ey-s}, p3:{x:ex,y:ey} }
  }
  // Back-flow: arc below via bottom edges
  const sx    = src.x + MACHINE_W / 2, sy = src.y + MACHINE_H
  const ex    = dst.x + MACHINE_W / 2, ey = dst.y + MACHINE_H
  const loopY = Math.max(sy, ey) + 55
  return { p0:{x:sx,y:sy}, p1:{x:sx,y:loopY}, p2:{x:ex,y:loopY}, p3:{x:ex,y:ey} }
}

/** Bezier from source diamond right-point → machine left-edge (always forward). */
function sourceToBezier(sourcePos: Pt, dst: MachineSnapshot): BezierSeg {
  const sx = sourcePos.x + DIAMOND_R, sy = sourcePos.y
  const ex = dst.x,                   ey = dst.y + MACHINE_H / 2
  const s  = Math.max(60, Math.abs(ex - sx) * 0.45)
  return { p0:{x:sx,y:sy}, p1:{x:sx+s,y:sy}, p2:{x:ex-s,y:ey}, p3:{x:ex,y:ey} }
}

/** Bezier from machine right-edge → sink diamond left-point (always forward). */
function machineToSinkBezier(src: MachineSnapshot, sinkPos: Pt): BezierSeg {
  const sx = src.x + MACHINE_W, sy = src.y + MACHINE_H / 2
  const ex = sinkPos.x - DIAMOND_R, ey = sinkPos.y
  const s  = Math.max(60, Math.abs(ex - sx) * 0.45)
  return { p0:{x:sx,y:sy}, p1:{x:sx+s,y:sy}, p2:{x:ex-s,y:ey}, p3:{x:ex,y:ey} }
}

/** "Order001" → "01", "Order12" → "12" */
function shortLabel(orderId: string): string {
  const m = orderId.match(/(\d+)$/)
  return m ? m[1].slice(-2).padStart(2, '0') : orderId.slice(-2)
}

// ── ViewBox fitting ───────────────────────────────────────────────────────────
function computeViewBox(machines: MachineSnapshot[]): string {
  if (machines.length === 0) return '0 0 800 500'
  const xs = machines.map((m) => m.x)
  const ys = machines.map((m) => m.y)
  const x0 = Math.min(...xs) - PAD_LEFT          // extended left for source node
  const y0 = Math.min(...ys) - PAD
  const x1 = Math.max(...xs) + MACHINE_W + PAD_RIGHT
  const y1 = Math.max(...ys) + MACHINE_H + SUB_ROWS_H + PAD
  return `${x0} ${y0} ${x1 - x0} ${y1 - y0}`
}

// ── Conveyor edge (now delegates to shared bezier helper) ─────────────────────
function ConveyorLine({
  from, to, machines,
}: {
  from: string
  to: string
  machines: Map<string, MachineSnapshot>
}) {
  const src = machines.get(from)
  const dst = machines.get(to)
  if (!src || !dst) return null
  const { p0, p1, p2, p3 } = machineToMachineBezier(src, dst)
  return (
    <path
      d={`M ${p0.x} ${p0.y} C ${p1.x} ${p1.y}, ${p2.x} ${p2.y}, ${p3.x} ${p3.y}`}
      fill="none"
      stroke="#1e3a5f"
      strokeWidth={1.5}
      strokeDasharray="5 3"
      markerEnd="url(#arrow)"
      opacity={0.75}
    />
  )
}

// ── Machine node ──────────────────────────────────────────────────────────────
function MachineNode({
  m, onClick,
}: {
  m: MachineSnapshot
  onClick: (id: string) => void
}) {
  const fill      = FILL[m.state]   ?? FILL.idle
  const stroke    = STROKE[m.state] ?? STROKE.idle
  const labelCol  = LABEL_COLOR[m.state] ?? LABEL_COLOR.idle
  const badge     = STATE_BADGE[m.state] ?? m.state.toUpperCase()
  const utilPct   = Math.round(m.utilization * 100)
  const isOffShift = m.state === 'off_shift'
  const isFailed   = m.state === 'failed'
  const isBusy     = m.state === 'busy'
  const isSetup    = m.state === 'setup'

  const ordShort   = m.job_in_service
    ? m.job_in_service.replace(/^[Oo]rder_?/i, '').slice(-6)
    : null
  const queueDepth = m.jobs_in_queue.length
  const fixLabel   = m.fixture_id ? m.fixture_id.slice(-10) : null

  return (
    <g
      transform={`translate(${m.x}, ${m.y})`}
      onClick={() => onClick(m.id)}
      style={{ cursor: 'pointer' }}
    >
      {/* Machine body */}
      <rect
        x={0} y={0} width={MACHINE_W} height={MACHINE_H} rx={6}
        fill={fill} stroke={stroke}
        strokeWidth={isBusy || isFailed || isSetup ? 2 : 1}
        style={{ transition: 'fill 0.5s, stroke 0.5s' }}
      />

      {/* Off-shift diagonal stripe overlay — drawn after body so it's visible */}
      {isOffShift && (
        <rect
          x={0} y={0} width={MACHINE_W} height={MACHINE_H} rx={6}
          fill="url(#offShiftStripe)" opacity={0.35}
        />
      )}

      {/* Outer glow ring for busy / failed / setup */}
      {(isBusy || isFailed || isSetup) && (
        <rect
          x={-3} y={-3} width={MACHINE_W + 6} height={MACHINE_H + 6} rx={9}
          fill="none"
          stroke={isFailed ? '#f43f5e' : isSetup ? '#f59e0b' : '#2dd4bf'}
          strokeWidth={1.5} opacity={0.25}
        />
      )}

      {/* Machine ID label */}
      <text x={MACHINE_W / 2} y={18} textAnchor="middle"
        fill={labelCol} fontSize={10} fontFamily="JetBrains Mono, monospace" fontWeight="bold"
        style={{ transition: 'fill 0.5s' }}>
        {m.label}
      </text>

      {/* State badge */}
      <text x={MACHINE_W / 2} y={33} textAnchor="middle"
        fill={isOffShift ? '#374151' : labelCol} fontSize={8}
        fontFamily="JetBrains Mono, monospace" letterSpacing="0.05em"
        opacity={isOffShift ? 0.7 : 1}
        style={{ transition: 'fill 0.5s' }}>
        {badge}
      </text>

      {/* Utilisation bar */}
      {!isOffShift && (
        <>
          <rect x={8} y={46} width={MACHINE_W - 16} height={5} rx={2} fill="#0f172a" />
          <rect x={8} y={46}
            width={Math.max(0, (MACHINE_W - 16) * m.utilization)}
            height={5} rx={2}
            fill={isFailed ? '#f43f5e' : isBusy ? '#2dd4bf' : isSetup ? '#f59e0b' : '#334155'}
            style={{ transition: 'width 0.6s, fill 0.5s' }}
          />
          <text x={MACHINE_W - 8} y={60} textAnchor="end"
            fill={labelCol} fontSize={8} fontFamily="JetBrains Mono, monospace" opacity={0.6}>
            {utilPct}%
          </text>
        </>
      )}

      {/* ── Row 1: In-service pill + queue badge ── */}
      {!isOffShift && ordShort && (
        <g transform={`translate(4, ${INFO_ROW_Y})`} className="entity-bubble">
          <rect width={PILL_W} height={PILL_H} rx={4}
            fill="#0d4a43" stroke="#2dd4bf" strokeWidth={1} />
          <text x={7} y={11} fill="#ccfbf1" fontSize={8}
            fontFamily="JetBrains Mono, monospace" fontWeight="bold">
            ▶ {ordShort}
          </text>
          {queueDepth > 0 && (
            <g transform={`translate(${PILL_W + 5}, 0)`}>
              <rect width={30} height={PILL_H} rx={4}
                fill="#1e3a5f" stroke="#334155" strokeWidth={1} />
              <text x={15} y={11} textAnchor="middle" fill="#94a3b8" fontSize={8}
                fontFamily="JetBrains Mono, monospace">
                +{queueDepth}Q
              </text>
            </g>
          )}
        </g>
      )}

      {/* ── Row 2: Fixture + worker badges ── */}
      {!isOffShift && (fixLabel || m.has_worker) && (
        <g transform={`translate(4, ${BADGE_ROW_Y})`}>
          {fixLabel && (
            <>
              <rect width={64} height={13} rx={3}
                fill="#1c1a2e" stroke="#6d28d9" strokeWidth={1} opacity={0.9} />
              <text x={4} y={9.5} fill="#a78bfa" fontSize={7}
                fontFamily="JetBrains Mono, monospace">
                {fixLabel}
              </text>
            </>
          )}
          {m.has_worker && (
            <g transform={`translate(${fixLabel ? 70 : 0}, 0)`}>
              <rect width={24} height={13} rx={3}
                fill="#1a2e1c" stroke="#16a34a" strokeWidth={1} opacity={0.9} />
              <text x={12} y={9.5} textAnchor="middle" fill="#4ade80" fontSize={9}
                fontFamily="JetBrains Mono, monospace">
                ⚙
              </text>
            </g>
          )}
        </g>
      )}
    </g>
  )
}

// ── Source diamond ────────────────────────────────────────────────────────────
function SourceNode({ pos }: { pos: Pt }) {
  const r = DIAMOND_R
  return (
    <g transform={`translate(${pos.x}, ${pos.y})`}>
      <polygon
        points={`0,${-r} ${r},0 0,${r} ${-r},0`}
        fill="#0c2340" stroke="#2dd4bf" strokeWidth={1.5}
      />
      <text textAnchor="middle" y={4}
        fill="#2dd4bf" fontSize={5.5}
        fontFamily="JetBrains Mono, monospace" fontWeight="bold">
        IN
      </text>
    </g>
  )
}

// ── Sink diamond ──────────────────────────────────────────────────────────────
function SinkNode({ pos }: { pos: Pt }) {
  const r = DIAMOND_R
  return (
    <g transform={`translate(${pos.x}, ${pos.y})`}>
      <polygon
        points={`0,${-r} ${r},0 0,${r} ${-r},0`}
        fill="#1a0d2e" stroke="#a78bfa" strokeWidth={1.5}
      />
      <text textAnchor="middle" y={4}
        fill="#a78bfa" fontSize={5.5}
        fontFamily="JetBrains Mono, monospace" fontWeight="bold">
        OUT
      </text>
    </g>
  )
}

// Track which order IDs have already had a sink transit spawned (dedup across re-renders)
const _markedSinkTransit = new Set<string>()

// ── Order particles ───────────────────────────────────────────────────────────
/**
 * Renders three layers of visual feedback for order movement:
 *
 *  1. Transit circles  — fly along the conveyor bezier when a machine changes
 *  2. Static circles   — hover above the machine while the order is in-service
 *  3. Queued dots      — pulse at the NEXT machine's entry while waiting in queue
 *
 * All animation is skipped at full speed (rtFactor === 0).
 */
function OrderParticles({
  machineMap,
  sourcePos,
  sinkPos,
}: {
  machineMap: Map<string, MachineSnapshot>
  sourcePos: Pt
  sinkPos: Pt
}) {
  const {
    activeJobs, startedOrders, completedOrders, instanceConfig, rtFactor,
  } = useSimStore()

  type PrevInfo = { machineId: string; routingRow: number; family: string }
  const prevStateRef = useRef<Map<string, PrevInfo>>(new Map())
  const transitsRef  = useRef<Transit[]>([])
  // Incrementing this triggers a re-render from inside the RAF loop
  const [, setTick]  = useState(0)

  // ── Spawn transits when activeJobs changes ──────────────────────────────────
  useEffect(() => {
    if (rtFactor === 0) {
      prevStateRef.current.clear()
      transitsRef.current = []
      _markedSinkTransit.clear()
      return
    }
    // New run: completedOrders map is reset to empty — clear sink dedup set
    if (completedOrders.size === 0) _markedSinkTransit.clear()
    const now  = performance.now()
    const prev = prevStateRef.current

    for (const job of activeJobs) {
      const prevInfo   = prev.get(job.order_id)
      const dstMachine = machineMap.get(job.machine_id)
      if (!dstMachine) continue

      if (!prevInfo) {
        // First appearance: source diamond → first machine
        transitsRef.current.push({
          key:       `${job.order_id}-src-${now}`,
          orderId:   job.order_id,
          label:     shortLabel(job.order_id),
          colour:    familyColour(job.family),
          seg:       sourceToBezier(sourcePos, dstMachine),
          startedAt: now,
          duration:  TRANSIT_MS,
        })
      } else if (prevInfo.machineId !== job.machine_id) {
        // Machine changed: old machine → new machine
        const srcMachine = machineMap.get(prevInfo.machineId)
        if (srcMachine) {
          transitsRef.current.push({
            key:       `${job.order_id}-${prevInfo.machineId}-${job.machine_id}-${now}`,
            orderId:   job.order_id,
            label:     shortLabel(job.order_id),
            colour:    familyColour(job.family),
            seg:       machineToMachineBezier(srcMachine, dstMachine),
            startedAt: now,
            duration:  TRANSIT_MS,
          })
        }
      }

      prev.set(job.order_id, {
        machineId:  job.machine_id,
        routingRow: job.routing_row,
        family:     job.family,
      })
    }

    // Spawn transit to SINK for newly completed orders (last machine → sink)
    for (const [id] of completedOrders.entries()) {
      if (_markedSinkTransit.has(id)) continue
      const prevInfo = prev.get(id)
      if (prevInfo) {
        const srcMachine = machineMap.get(prevInfo.machineId)
        if (srcMachine) {
          _markedSinkTransit.add(id)
          transitsRef.current.push({
            key:       `${id}-sink-${now}`,
            orderId:   id,
            label:     shortLabel(id),
            colour:    familyColour(prevInfo.family),
            seg:       machineToSinkBezier(srcMachine, sinkPos),
            startedAt: now,
            duration:  TRANSIT_MS,
          })
        }
      }
      prev.delete(id)
    }
  }, [activeJobs, completedOrders, rtFactor])  // eslint-disable-line react-hooks/exhaustive-deps

  // ── RAF loop: advances transit animations, re-renders while particles fly ──
  useEffect(() => {
    if (rtFactor === 0) return
    let rafId: number
    const loop = () => {
      const prevLen = transitsRef.current.length
      const now     = performance.now()
      transitsRef.current = transitsRef.current.filter(
        (t) => now - t.startedAt < t.duration,
      )
      // Re-render while flying, and once more when the last transit clears
      if (transitsRef.current.length > 0 || prevLen !== transitsRef.current.length) {
        setTick((n) => n + 1)
      }
      rafId = requestAnimationFrame(loop)
    }
    rafId = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(rafId)
  }, [rtFactor])

  if (rtFactor === 0) return null

  const now             = performance.now()
  const activeOrderIds  = new Set(activeJobs.map((j) => j.order_id))
  const flyingOrderIds  = new Set(transitsRef.current.map((t) => t.orderId))

  // ── Layer 1: queued dots ────────────────────────────────────────────────────
  // Orders in startedOrders but absent from activeJobs — sitting in a machine queue.
  // Show a pulsing dot at the NEXT machine's left entry.
  const queuedDots = [...startedOrders.entries()]
    .filter(([id]) =>
      !activeOrderIds.has(id) &&
      !completedOrders.has(id) &&
      !flyingOrderIds.has(id),   // don't double-render while transit is in flight
    )
    .flatMap(([orderId]) => {
      const prevInfo = prevStateRef.current.get(orderId)
      if (!prevInfo) return []
      const nextRow       = prevInfo.routingRow + 1
      const orderCfg      = instanceConfig?.orders.find((o) => o.order_id === orderId)
      const nextMachineId = orderCfg?.routing[nextRow]?.eligible_machines[0]
      const nextMachine   = nextMachineId ? machineMap.get(nextMachineId) : null
      if (!nextMachine) return []
      return [{
        key:    `queued-${orderId}`,
        label:  shortLabel(orderId),
        colour: familyColour(prevInfo.family),
        // Left entry of next machine, slightly below centre-line
        x: nextMachine.x - 18,
        y: nextMachine.y + MACHINE_H / 2 + 10,
      }]
    })

  // ── Layer 2: static circles ─────────────────────────────────────────────────
  // Orders currently being processed — hover above the machine box.
  const staticCircles = activeJobs
    .filter((j) => !flyingOrderIds.has(j.order_id))
    .flatMap((j) => {
      const m = machineMap.get(j.machine_id)
      if (!m) return []
      return [{
        key:    `static-${j.order_id}`,
        label:  shortLabel(j.order_id),
        colour: familyColour(j.family),
        x: m.x + MACHINE_W / 2,
        y: m.y - 14,    // float above the machine box top edge
      }]
    })

  // ── Layer 3: transit circles ────────────────────────────────────────────────
  const transitCircles = transitsRef.current.map((t) => {
    const raw = Math.min(1, (now - t.startedAt) / t.duration)
    const pos = evalBezier(smoothstep(raw), t.seg)
    return { key: t.key, label: t.label, colour: t.colour, ...pos }
  })

  return (
    <g style={{ pointerEvents: 'none' }}>

      {/* ── Queued dots — pulsing at next machine entry ── */}
      {queuedDots.map((dot) => (
        <g key={dot.key}>
          <circle cx={dot.x} cy={dot.y} r={5.5} fill={dot.colour}>
            <animate attributeName="r"       values="4;6;4"     dur="1.4s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.3;0.65;0.3" dur="1.4s" repeatCount="indefinite" />
          </circle>
          <text x={dot.x} y={dot.y + 3.5} textAnchor="middle"
            fill="#fff" fontSize={5} fontFamily="JetBrains Mono, monospace" fontWeight="bold">
            {dot.label}
          </text>
        </g>
      ))}

      {/* ── Static circles — order in-service on machine ── */}
      {staticCircles.map((c) => (
        <g key={c.key}>
          <circle cx={c.x} cy={c.y} r={9} fill={c.colour} opacity={0.88} />
          <text x={c.x} y={c.y + 3.5} textAnchor="middle"
            fill="#0d1117" fontSize={7} fontFamily="JetBrains Mono, monospace" fontWeight="bold">
            {c.label}
          </text>
        </g>
      ))}

      {/* ── Transit circles — flying along bezier path ── */}
      {transitCircles.map((p) => (
        <g key={p.key}>
          {/* Soft glow halo */}
          <circle cx={p.x} cy={p.y} r={14} fill={p.colour} opacity={0.13} />
          {/* Main circle */}
          <circle cx={p.x} cy={p.y} r={9} fill={p.colour} opacity={0.95} />
          <text x={p.x} y={p.y + 3.5} textAnchor="middle"
            fill="#0d1117" fontSize={7} fontFamily="JetBrains Mono, monospace" fontWeight="bold">
            {p.label}
          </text>
        </g>
      ))}

    </g>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
interface Props {
  snapshot: FactorySnapshot
}

export function FactorySVG({ snapshot }: Props) {
  const setInspector = useSimStore((s) => s.setInspector)

  const machineMap = useMemo(
    () => new Map(snapshot.machines.map((m) => [m.id, m])),
    [snapshot.machines],
  )
  const viewBox = useMemo(() => computeViewBox(snapshot.machines), [snapshot.machines])

  // Source node: centred vertically, PAD_LEFT/2 to the left of the leftmost machine
  const sourcePos = useMemo((): Pt => {
    if (snapshot.machines.length === 0) return { x: 0, y: 0 }
    const minX = Math.min(...snapshot.machines.map((m) => m.x))
    const avgY  = snapshot.machines.reduce((s, m) => s + m.y + MACHINE_H / 2, 0)
                  / snapshot.machines.length
    return { x: minX - PAD_LEFT / 2, y: avgY }
  }, [snapshot.machines])

  // Sink node: centred vertically, PAD_RIGHT/2 to the right of the rightmost machine
  const sinkPos = useMemo((): Pt => {
    if (snapshot.machines.length === 0) return { x: 0, y: 0 }
    const maxX = Math.max(...snapshot.machines.map((m) => m.x))
    const avgY  = snapshot.machines.reduce((s, m) => s + m.y + MACHINE_H / 2, 0)
                  / snapshot.machines.length
    return { x: maxX + MACHINE_W + PAD_RIGHT / 2, y: avgY }
  }, [snapshot.machines])

  const handleMachineClick = (id: string) => setInspector({ kind: 'machine', id })

  return (
    <>
      <style>{`
        .entity-bubble { animation: bubble-pop 0.18s ease-out; }
        @keyframes bubble-pop {
          from { transform: scale(0.6); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
      `}</style>

      <svg
        viewBox={viewBox}
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
        style={{ background: 'transparent' }}
      >
        <defs>
          <marker id="arrow" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
            <polygon points="0 0, 7 2.5, 0 5" fill="#1e3a5f" />
          </marker>

          <pattern id="offShiftStripe" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="8" stroke="#1f2937" strokeWidth="4" />
          </pattern>

          <pattern id="dotGrid" patternUnits="userSpaceOnUse" width="30" height="30">
            <circle cx="1" cy="1" r="0.8" fill="#1e293b" opacity="0.5" />
          </pattern>
        </defs>

        {/* Background grid */}
        <rect x="-9999" y="-9999" width="99999" height="99999" fill="url(#dotGrid)" />

        {/* Conveyor edges (behind machine nodes) */}
        {snapshot.conveyors.map((edge, i) => (
          <ConveyorLine
            key={i}
            from={edge.from_machine}
            to={edge.to_machine}
            machines={machineMap}
          />
        ))}

        {/* Machine nodes */}
        {snapshot.machines.map((m) => (
          <MachineNode key={m.id} m={m} onClick={handleMachineClick} />
        ))}

        {/* Source input diamond */}
        <SourceNode pos={sourcePos} />

        {/* Sink output diamond */}
        <SinkNode pos={sinkPos} />

        {/* Order particles — rendered on top of everything */}
        <OrderParticles machineMap={machineMap} sourcePos={sourcePos} sinkPos={sinkPos} />
      </svg>
    </>
  )
}
